import { BehaviorSubject, Subject } from 'rxjs';

import { EPollsterPollMode } from './pollster.const';
import { IPollster, IPollsterOptions, IPollsterPollParams } from './pollster.types';

const FIVE_SEC :number = 5 * 1000;
const FIVE_MIN :number = 5 * 60 * 1000;

/**
 * @example <caption>Minimal usage</caption>
 * const pollster = new Pollster({
 *   onPoll: (params :IPollsterPollParams) => {
 *     console.log(params);
 *   },
 * });
 * pollster.start();
 * @example <caption>Fast and Slow intervals</caption>
 * const pollster = new Pollster({
 *   poll: (params :IPollsterPollParams) => {
 *     if (params.type === EPollsterPollType.FAST) {
 *       foo.fetchUpdates();
 *       foo.fetchWhetherUpdatesExist(); // sets foo.updatesExist = true if there are updates to fetch
 *     } else {
 *       foo.fetchWhetherUpdatesExist();
 *     }
 *   },
 *   shouldPollFast: () => foo.updatesExist,
 * });
 * pollster.start();
 */
export class Pollster implements IPollster {
  #interval :NodeJS.Timeout;
  #options :IPollsterOptions;

  #timeFast :number = FIVE_SEC;
  #timeSlow :number = FIVE_MIN;

  #error$ = new Subject<Error>();
  #isPolling$ :BehaviorSubject<boolean> = new BehaviorSubject(false);
  #poll$ = new Subject<IPollsterPollParams>();
  #start$ = new Subject<void>();
  #stop$ = new Subject<void>();

  latestError :Error;

  error$ = this.#error$.asObservable();
  isPolling$ = this.#isPolling$.asObservable();
  poll$ = this.#poll$.asObservable();
  start$ = this.#start$.asObservable();
  stop$ = this.#stop$.asObservable();

  constructor(options ?:IPollsterOptions) {
    this.#options = {
      shouldPollFast: true,
      interval: [FIVE_SEC, FIVE_MIN],
      ...options,
    };

    this.#updateAfterOptionsSet();
  }

  isPolling() :boolean {
    return this.#isPolling$.getValue();
  }

  setOptions(options :IPollsterOptions) :void {
    this.#options = {
      ...this.#options,
      ...options,
    };

    this.#updateAfterOptionsSet();

    if (this.isPolling()) {
      this.stop();
      this.start();
    }
  }

  start() {
    if (this.isPolling()) this.stop();

    this.#isPolling$.next(true);
    this.#onStart();

    if (this.#shouldPollFast()) this.#pollFast();
    else this.#pollSlow();
  }

  stop() {
    const wasPolling = this.isPolling();

    clearInterval(this.#interval);
    this.#isPolling$.next(false);

    if (wasPolling) this.#onStop(); // only stop if we were polling
  }

  #break(error :Error) :void {
    this.latestError = error;
    this.stop();
    this.#onError(error);
  }

  #onError(error :Error) :void {
    this.#options.onError?.(error);
    this.#error$.next(error);
  }
  #onPoll(params :IPollsterPollParams) :void {
    try {
      this.#options.onPoll?.(params);
      this.#poll$.next(params);
    } catch (error) {
      this.#break(error);
    }
  }
  #onStart() :void {
    this.#options.onStart?.();
    this.#start$.next();
  }
  #onStop() :void {
    this.#options.onStop?.();
    this.#stop$.next();
  }

  #pollFast() {
    clearInterval(this.#interval);

    const params :IPollsterPollParams = { mode: EPollsterPollMode.FAST };

    this.#onPoll(params); // "leading edge" call
    this.#interval = setInterval(() => {
      if (!this.#shouldPollFast()) {
        this.#pollSlow();
      } else {
        this.#onPoll(params);
      }
    }, this.#timeFast);
  }
  #pollSlow() {
    clearInterval(this.#interval);

    const params :IPollsterPollParams = { mode: EPollsterPollMode.SLOW };

    this.#onPoll(params); // "leading edge" call
    this.#interval = setInterval(() => {
      if (this.#shouldPollFast()) {
        this.#pollFast();
      } else {
        this.#onPoll(params);
      }
    }, this.#timeSlow);
  }

  #shouldPollFast() :boolean {
    return typeof this.#options.shouldPollFast === 'boolean'
      ? this.#options.shouldPollFast
      : (this.#options.shouldPollFast?.() ?? true);
  }

  #updateAfterOptionsSet() :void {
    if (Array.isArray(this.#options.interval)) {
      this.#timeFast = this.#options.interval[0];
      this.#timeSlow = this.#options.interval[1];
    } else if (typeof this.#options.interval === 'number') {
      this.#timeFast = this.#options.interval;
      this.#timeSlow = this.#options.interval;
    }
  }
}
